import axios from 'axios'
import * as analytics from 'lib/analytics'
import type { AnalyticsEvent } from 'lib/analytics'

// SCI-3935. The sign-up funnel had no failure signal in PostHog: every submit
// failure went to Sentry (and only for 500/unknown) or nowhere, so an outage in
// account creation was invisible for alerting. This mirrors `client/lib/search-analytics.ts`
// (SCI-3810): `Funnel: signup_error` is what error alerts fire on, `Funnel: signup_success`
// is the matched denominator so a rate can be computed as
// signup_error / (signup_error + signup_success). Both fire from the submit saga —
// emit one without the other and the denominator lies.
//
// `mode` is 'register' | 'login': one submit saga powers both, and a returning user
// logging in to start a trial is as much part of the funnel as a new sign-up. Alerts
// that only care about new-account outages filter on `mode = 'register'`.
//
// Server-side trial/checkout failures are captured separately (see
// `Funnel: trial_error` / `Funnel: checkout_error` in server/controllers/subscriptions.js),
// because the trial is a full-page form POST with no JS in the loop to catch a failure.
// 'saml' only reaches the signup_type step — the enterprise SAML form uses it to
// discover an org's IdP, then hands off to that IdP rather than submitting here.
// 'email_verify' is the post-registration hold: the account exists but is unusable
// until the link is clicked. It is its own mode because both a register and a login
// attempt land there (the SUBMIT error handler sets it for either), so by the time
// the user asks to resend, the mode that got them there is no longer recoverable.
export type SignupMode = 'register' | 'login' | 'saml' | 'email_verify'

// Register is a two-step form. Step 0 calls `/users/signup-type` to detect an SSO or
// existing account; only step 1 posts to `/users/register`. A `signup_type` failure
// strands the user on step 0 where they can never reach the register call — so it
// would never show up as a `submit` error. Both steps report, and each has its own
// matched success, so an error rate can be computed per step rather than mixing a
// hard blocker into the submit denominator.
//
// 'resend_verification' is the same idea one step later: it happens after the account
// already exists, so it has no matching `submit` success and must stay out of that
// step's denominator.
export type SignupStep = 'signup_type' | 'submit' | 'resend_verification'

// Groups every funnel outcome event (client sign-up + server trial/checkout) under
// one PostHog prefix. sendEventPostHog builds the name as `${category}: ${action}`.
const CATEGORY = 'Funnel'

interface ErrorFacts {
  status: number | null
  // Application error type/code from the API body, e.g. 'emailexists',
  // 'invalid_email', 'request_blocked', 'EmailVerificationRequiredException'.
  type: string | null
  code: string | null
  // Error class name (TypeError, AxiosError, ...) — kept off `label` so a bare JS
  // exception surfaces its message rather than the useless class name.
  name: string | null
  message: string | null
  detail: string | null
  timedOut: boolean
  // A system failure worth paging on, as opposed to a user-correctable rejection
  // (duplicate email, validation). True for a 5xx or a request that got no response
  // (network/timeout), or any non-HTTP JS error. Lets an alert filter to the
  // actionable subset without enumerating every 4xx type.
  unexpected: boolean
}

const errorFacts = (e: unknown): ErrorFacts => {
  if (axios.isAxiosError(e)) {
    // Some blocks (CloudFront) return a string body rather than a JSON object.
    const data = (typeof e.response?.data === 'object' ? e.response?.data : undefined) as
      | { type?: string; code?: string; message?: string; detail?: string; error?: string }
      | undefined
    const status = e.response?.status ?? null

    return {
      status,
      type: data?.type || null,
      code: data?.code || e.code || null,
      name: e.name || null,
      message: data?.message || e.message || null,
      detail: data?.detail || data?.error || null,
      // The two shapes of "the request never came back": the API's own gateway
      // timeout, and a request that got no response at all.
      timedOut: status === 504 || (!e.response && (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT')),
      unexpected: status === null || status >= 500
    }
  }

  if (e instanceof Error) {
    return {
      status: null,
      type: null,
      code: null,
      name: e.name || null,
      message: e.message || null,
      detail: null,
      timedOut: false,
      unexpected: true
    }
  }

  return {
    status: null,
    type: null,
    code: null,
    name: null,
    message: null,
    detail: null,
    timedOut: false,
    unexpected: true
  }
}

// The sign-up submit saga is not on a paint-sensitive path the way search is, but
// `trackSignupError` runs inside errorWrap's terminal `onError` handler, which is
// NOT wrapped in a try/catch — a throw out of a wedged analytics script would take
// down the login-modal saga's takeLatest watcher. Deferring to a later macrotask and
// guarding the dispatch keeps a broken analytics call from ever reaching the saga.
//
// `defer: false` is for events that must survive an imminent navigation. The submit
// saga hands off to Stripe with a real form POST, and a queued macrotask does not
// outlive that. `dispatch` swallows its own errors, so sending inline is still safe
// inside the saga.
const send = (payload: AnalyticsEvent, { defer = true }: { defer?: boolean } = {}): void => {
  const dispatch = () => {
    try {
      analytics.event(payload)
    } catch (e) {
      console.error('Failed to report a sign-up outcome', e)
    }
  }

  if (typeof window === 'undefined' || !defer) {
    dispatch()
    return
  }

  window.setTimeout(dispatch, 0)
}

// `extra` carries the checkout intent recorded on /pricing. That is what makes a
// vanished Stripe hand-off detectable: a signup_success with an intent, followed by no
// server-side `trial_checkout_started` / `checkout_started`, is a hand-off that never
// landed. The plan also says which endpoint was headed for — team is a paid checkout,
// anything else is a trial — so no separate client beacon is needed to tell them apart.
export const trackSignupSuccess = (
  mode: SignupMode,
  step: SignupStep = 'submit',
  extra: Record<string, unknown> = {}
): void =>
  // Sent inline: on the checkout path this event is followed by a full-page form POST,
  // and it is the client half of the hand-off correlation — losing it to the
  // navigation would leave a vanished hand-off looking like a user who never tried.
  send({ category: CATEGORY, action: 'signup_success', mode, step, ...extra }, { defer: false })

// A sign-up attempt rejected by our own form before any request goes out (passwords
// not matching). There is no Error to unpack and nothing broke, so it is reported as
// an expected failure — it still belongs in the funnel, because a user stuck here
// never reaches the API and would otherwise just look like an abandonment.
export const trackSignupClientError = (mode: SignupMode, reason: string, step: SignupStep = 'submit'): void =>
  send({
    category: CATEGORY,
    action: 'signup_error',
    mode,
    step,
    label: reason,
    type: reason,
    status: null,
    clientSide: true,
    timedOut: false,
    unexpected: false
  })

export const trackSignupError = (
  mode: SignupMode,
  e: unknown,
  extra: Record<string, unknown> = {},
  step: SignupStep = 'submit'
): void => {
  const { status, type, code, name, message, detail, timedOut, unexpected } = errorFacts(e)

  send({
    category: CATEGORY,
    action: 'signup_error',
    mode,
    step,
    // Google Analytics forwards only category and label, so the label has to be the
    // one string that identifies the failure. Everything else rides along as a
    // PostHog property.
    label: type || code || detail || message || `status code: ${status ?? 'none'}`,
    status,
    type,
    code,
    timedOut,
    unexpected,
    error: { name, message, detail, status, type },
    ...extra
  })
}
